<script setup lang="ts">
  import { Route } from "@/constants/route"
  import { useEvChargerQuote as defaultUseEvChargerQuote } from "@/lib/useEvChargerQuote"
  import { useStepWizard as defaultUseStepWizard } from "@/lib/useStepWizard"
  import { inject, ref, watch } from "vue"
  import { useRouter as defaultUseRouter } from "vue-router"
  import { Alert, AlertDescription } from "@/components/ui/alert"
  import Button from "@/components/ui/button/Button.vue"
  import {
    RadioButton,
    RadioButtonGroup,
  } from "@/components/ui/inputs/radio-button-group"
  import { StepWizardProgressCard } from "@/components/ui/step-wizard"

  type EvChargerParkingOptions = "yes" | "no" | undefined

  const useEvChargerQuote = inject(
    "useEvChargerQuote",
    defaultUseEvChargerQuote,
  )
  const useStepWizard = inject("useStepWizard", defaultUseStepWizard)
  const useRouter = inject("useRouter", defaultUseRouter)

  const router = useRouter()
  const { goNext } = useStepWizard()
  const { updateParking } = useEvChargerQuote()

  const hasOffStreetParking = ref<EvChargerParkingOptions>()
  const showParkingAlert = ref(false)

  watch(hasOffStreetParking, (newVal) => {
    if (newVal !== undefined && showParkingAlert.value !== false) {
      showParkingAlert.value = false
    }
  })

  const onSubmit = () => {
    if (hasOffStreetParking.value === "yes") {
      updateParking(true)
      goNext()
    } else if (hasOffStreetParking.value === "no") {
      router.push(Route.evChargerQuoteNoParking)
    } else {
      showParkingAlert.value = true
    }
  }
</script>

<template>
  <form
    class="flex w-full flex-wrap justify-center"
    novalidate
    @submit.prevent="onSubmit"
  >
    <StepWizardProgressCard id="off-street-parking" title="Your home">
      <p class="body-text-strong mb-3 pt-1 sm:mb-4">
        Do you have off-street parking?
      </p>
      <p class="mb-8 sm:mb-6">
        We can only install an EV charger if you have off-street parking, such
        as a driveway or garage.
      </p>
      <RadioButtonGroup
        v-model="hasOffStreetParking"
        aria-labelledby="off-street-parking"
        name="hasOffStreetParking"
        class="mb-5 pt-2 sm:mb-3"
      >
        <RadioButton
          name="offStreetParkingYes"
          value="yes"
          label="Yes, I have off-street parking"
          data-testid="yes-off-street-parking"
        >
        </RadioButton>
        <RadioButton
          name="offStreetParkingNo"
          value="no"
          label="No, I don't have off-street parking"
          data-testid="no-off-street-parking"
        >
        </RadioButton>
      </RadioButtonGroup>
      <Alert v-if="showParkingAlert" variant="error" class="mt-4 sm:mt-6">
        <AlertDescription>
          <p class="text-secondary-500">
            Please choose an option about off-street parking to continue.
          </p>
        </AlertDescription>
      </Alert>
      <template #next_button>
        <Button variant="tonal" type="submit" size="md"> Next </Button>
      </template>
    </StepWizardProgressCard>
  </form>
</template>
